/**
 * What's nearby: whether a venue passes the ticked selection keys under the
 * rules search applies (DEV-630). Pure, DOM-free — safe for native.
 */
import { parseKey, type SelectionKey } from "./accessibilitySelection";

export interface MatchVenue {
  /** `"<section id>:<tag id>"` for each filter feature the venue recorded. */
  sectionKeys: string[];
  /** Every tag id the venue recorded. */
  tagIds: number[];
}

export interface MatchSection {
  id: number;
  kind?: "basic" | "complex";
  tagIds: number[];
}

/**
 * Basic-section ticks match basic venues and complex-section ticks complex venues, the two kinds
 * being alternatives; tag-only ticks keep the step-free family OR'd and everything else AND'd.
 */
export function matchesSectionSelection(
  keys: SelectionKey[],
  venue: MatchVenue,
  sections: MatchSection[],
  familyIds: number[],
): boolean {
  const sectionById = new Map(sections.map((section) => [section.id, section]));
  const basic = new Map<number, number[]>();
  const complex = new Map<number, number[]>();
  const tagIds: number[] = [];

  for (const key of keys) {
    const parsed = parseKey(key);
    if (!parsed) continue;
    if (!tagIds.includes(parsed.tagId)) tagIds.push(parsed.tagId);
    if (parsed.headingId === null) continue;

    // A pair for a section this list does not offer (e.g. carried over from search) counts as tag-only.
    const section = sectionById.get(parsed.headingId);
    if (!section || !section.tagIds.includes(parsed.tagId)) continue;

    const byTag = section.kind === "complex" ? complex : basic;
    const sectionIds = byTag.get(parsed.tagId) ?? [];
    if (!sectionIds.includes(section.id)) sectionIds.push(section.id);
    byTag.set(parsed.tagId, sectionIds);
  }

  const venueKeys = new Set(venue.sectionKeys);
  const venueTags = new Set(venue.tagIds);
  const hasTag = (tagId: number) => venueTags.has(tagId);
  const hasPairs = (tagId: number, sectionIds: number[]) =>
    sectionIds.every((sectionId) => venueKeys.has(`${sectionId}:${tagId}`));
  const isFamily = (tagId: number) => familyIds.includes(tagId);

  const tagOnly = tagIds.filter((tagId) => !basic.has(tagId) && !complex.has(tagId));
  const tagOnlyFamily = tagOnly.filter(isFamily);
  if (!tagOnly.filter((tagId) => !isFamily(tagId)).every(hasTag)) return false;

  const tagOnlyFamilyHolds = tagOnlyFamily.length === 0 || tagOnlyFamily.some(hasTag);
  if (basic.size === 0 && complex.size === 0) return tagOnlyFamilyHolds;

  let basicHolds = false;
  if (basic.size > 0) {
    let required = true;
    const alternatives = tagOnlyFamily.map(hasTag);
    basic.forEach((sectionIds, tagId) => {
      // A paired family member stays an alternative, never a second requirement.
      if (isFamily(tagId)) alternatives.push(hasPairs(tagId, sectionIds));
      else required = required && hasPairs(tagId, sectionIds);
    });
    basicHolds = required && (alternatives.length === 0 || alternatives.some(Boolean));
  }

  let complexHolds = false;
  if (complex.size > 0) {
    // Complex sections list the step-free alternatives as plain checkboxes, so each tick is required.
    complexHolds =
      [...complex].every(([tagId, sectionIds]) => hasPairs(tagId, sectionIds)) && tagOnlyFamilyHolds;
  }

  return basicHolds || complexHolds;
}
